/**
 * How-to content — Executive (GM) and every Admin Panel section.
 *
 * Format (all guide files):
 *   key: { title, intro, tip?, faq?: [{ q, a }], sections: [
 *     { h: 'Task name', d: 'Why / when you do this',
 *       steps: [ 'Plain step', { t: 'Step text', tip: 'Extra help shown under the step' } ],
 *       result: 'What happens after you finish',
 *       mistakes: ['Common mistake to avoid'] } ] }
 *   Wrap a button, tab or field name in **double asterisks** to show it as a highlighted chip.
 */

export const BASICS = {
  h: 'Everyday basics (all staff)',
  d: 'Things every staff member uses every day, whatever their role.',
  steps: [
    { t: 'Open **m-hotels.co.ke/#/staff**, type your own **Username** and **Password**, then click **Sign In →**.', tip: 'Bookmark this address. The public website has no login button on purpose.' },
    { t: 'At the start of your shift open **Attendance** in the sidebar and click **Clock In**.', tip: 'Clock Out the same way when you leave. Forgot? Ask the admin to correct it.' },
    { t: 'Need time off? Open **My Leave** → **+ New Leave Request**, choose the type, the From and To dates and give a reason.', tip: 'HR is notified straight away and you get a notification when they decide.' },
    { t: 'Watch the **bell** at the top. A number means something needs you.', tip: 'Approvals = items waiting for your decision (tap one to jump to it). Alerts = low stock, ready orders, overdue tasks. History = everything already seen.' },
    'Use the **sun/moon** icon to switch between light and dark mode. Your choice is remembered on this device.',
    { t: 'When you leave a shared computer, click **Sign out** at the bottom of the sidebar.', tip: 'Everything you do is recorded under your name, so never leave your account open or share your password.' },
    { t: 'If a screen looks wrong, reload the page once. If it continues, tell the admin the exact message and what you clicked.' },
  ],
  result: 'You are clocked in, your leave is tracked and you never miss an approval or alert.',
  mistakes: ['Logging in with a colleague’s account — the audit trail will blame them for your actions.', 'Forgetting to clock out, which makes your hours wrong for payroll.'],
};

export const EXEC_GUIDES = {
  /* ═════════════════════════ GM DASHBOARD ═════════════════════════ */
  gm_dashboard: {
    title: 'GM Dashboard',
    intro: 'Your control room. In one screen you see today’s money, how full the hotel is, how the month is going and what needs a manager. Live figures refresh every minute on their own.',
    tip: 'Figures are only as good as what staff record: unconfirmed bills, unsettled folio charges and unrecorded expenses will not show.',
    faq: [
      { q: 'Charts say “No daily snapshots yet”', a: "Click 💾 Save today's snapshot. Trends appear after the first snapshot and grow each day you save." },
      { q: 'Revenue looks too low', a: 'Only confirmed payments count. Check the cashier Payment Queue for unconfirmed bills and Folios for unsettled charges.' },
      { q: 'Occupancy shows 0% but guests are in-house', a: 'Rooms only count as occupied when reception checks guests in through the system. Ask reception to check in walk-ins properly.' },
    ],
    sections: [
      { h: "Read today's four headline tiles", d: 'The quickest way to know how today is going.',
        steps: [
          { t: 'Look at **Revenue today**. It is all money confirmed today: rooms + food & bar + conference. The split is shown in small text under the figure.', tip: 'Money is counted only after the cashier confirms it, so a busy lunch may appear a few minutes later.' },
          { t: 'Look at **Occupancy**. It is occupied rooms ÷ rooms that can be sold. Rooms in Maintenance are left out.', tip: 'Colour tells you at a glance: green 75% or more, gold 45–74%, orange below 45%.' },
          { t: 'Look at **ADR** (average daily rate): room revenue ÷ occupied rooms — what an average occupied room earned today.' },
          { t: 'Look at **RevPAR** (revenue per available room): room revenue ÷ all sellable rooms. It combines price and occupancy in one number.', tip: 'If ADR is high but RevPAR is low, your prices are fine but too many rooms are empty.' },
        ],
        result: 'You know in seconds whether today is on track.',
      },
      { h: 'Check the month so far', d: 'See whether the month is making money.',
        steps: [
          'Read **MTD revenue** — every confirmed payment since the 1st of this month.',
          { t: 'Read **MTD expenses** — every expense recorded this month.', tip: 'It includes approved spoilage, approved complimentary bills, transport fuel and funds dispatched.' },
          'Read **MTD profit** and its margin (profit ÷ revenue). A ✅ means profit, ⚠️ means a loss so far.',
          'Read **Guests today** — how many checked in and out today.',
        ],
        result: 'You can tell owners at any moment whether the month is profitable.',
        mistakes: ['Judging profit early in the month — big expenses such as payroll usually land at month end.'],
      },
      { h: 'Use the charts', d: 'Spot trends and where money comes from.',
        steps: [
          { t: 'In **Revenue trend**, pick **7d**, **30d** or **90d** in the top-right of the chart.', tip: 'Hover a point to see the exact value for that day.' },
          'Compare with **Occupancy trend (%)** below it — rising occupancy with flat revenue means prices are dropping.',
          'Read the **Occupancy now** gauge and the **Revenue mix today** ring (rooms / F&B / conference).',
          'Read **Room status**: how many rooms are occupied, available and in maintenance right now.',
        ],
        result: 'You see patterns (weekends, events, slow days) and can plan promotions or staffing.',
      },
      { h: 'Act on “Needs attention”', d: 'Problems that need a manager today.',
        steps: [
          { t: 'Look at the **Needs attention** card. Each line shows a count; anything above 0 is coloured.', tip: 'Critical work orders = urgent breakdowns. Open work orders = repairs not finished. Overdue PM = missed routine maintenance. Open complaints = unhappy guests not yet resolved.' },
          'For work orders, ask maintenance (or open **Maintenance** if you are admin) for an update.',
          'For complaints, ask reception or marketing to resolve them in **Guest CRM** and close them with **✓ Resolve**.',
        ],
        result: 'Every number goes back to 0 as issues are fixed.',
        mistakes: ['Ignoring critical work orders — broken AC or water in a room loses revenue and reviews.'],
      },
      { h: 'Save the daily snapshot', d: 'Records each day so the trend charts fill up.',
        steps: [
          { t: 'Once a day — ideally at night audit around 23:00 — click **💾 Save today\'s snapshot** at the top right.', tip: 'It takes one second. A green message confirms it was saved.' },
          'If you save twice on the same day, the second save simply updates that day.',
          'The trend charts gain one new point for each day you save.',
        ],
        result: 'After a week you have a 7-day trend; after a month a full 30-day picture.',
        mistakes: ['Forgetting for several days — missed days stay blank in the charts.'],
      },
      { h: 'A simple daily routine', d: 'How to run the hotel from this screen.',
        steps: [
          'Morning: check Occupancy, guests arriving and leaving, and the Needs attention list. Brief heads of department.',
          'Midday: click **↻ Refresh** and compare revenue with the trend.',
          'Afternoon: check MTD profit and follow up on open complaints and work orders.',
          "Night: click **💾 Save today's snapshot**.",
        ],
        result: 'Nothing slips through and the history builds itself.',
      },
    ],
  },

  /* ═════════════════════════ ADMIN (main fallback) ═════════════════════════ */
  main_admin: {
    title: 'Admin Panel',
    intro: 'Pick any section from the grouped menu at the top of the Admin Panel. Each section shows its own guide underneath it.',
    sections: [
      { h: 'Find your way around', d: 'How the Admin Panel menu works.',
        steps: [
          'Sections are grouped: **Command Centre**, **Hotel Operations**, **Food & Beverage**, **Finance & Stock** and **System & Config**.',
          'Click any section name to open it. The path (e.g. Command Centre / Overview) shows which one you are in.',
          'Click **📘 How to use this page ↓** next to the path to jump to that section’s guide.',
        ],
      },
    ],
  },

  /* ═════════════════════════ ADMIN SECTIONS ═════════════════════════ */
  admin_overview: {
    title: 'Admin Overview',
    intro: 'The command centre: every important hotel figure on one screen, plus shortcuts into every module.',
    sections: [
      { h: 'Set up the hotel for the first time', d: 'Do these once, in this order, so nothing is missing.',
        steps: [
          { t: 'Open **Branding** (System & Config). Enter the hotel name, tagline, phone, email, address, upload the logo and click **Save**.', tip: 'The name appears on every screen, receipt, export and shared link.' },
          { t: 'Open **Rooms** and add every room — or use **Bulk Import** to load them all from a spreadsheet.' },
          'Open **Food Menu** and **Bar Menu** and add every item with its category and price.',
          { t: 'Open **Staff Accounts** and create one login per employee with the right role.', tip: 'Kitchen and chef accounts also need a Kitchen Station.' },
          'Open **Landing Page** and update the website text, photos, rooms and amenities.',
          'Open **Session Settings** and choose login rules (session length, single session, login hours).',
          'Open **Data Backup** and click **⬇ Download Backup Now**.',
        ],
        result: 'The hotel is ready: staff can log in, sell rooms and take orders.',
      },
      { h: 'Read the dashboard figures', d: 'What every number and chart means.',
        steps: [
          'Top tiles: **Today\'s revenue**, **Month revenue**, **Occupancy**, **Pending bills**, **Revenue (YTD)** and **Complimentaries**.',
          '**Revenue Trend — Last 30 Days** and **Monthly Revenue — 12 Months** show the direction of the business.',
          '**Payment Modes** shows how guests pay (cash, M-Pesa, card…).',
          '**Waiter Revenue Leaders** ranks waiters by sales; **Bills Status** shows pending, paid today and payment-requested bills.',
          '**Recent Transactions** and **Activity Feed** show the latest payments and actions.',
          { t: 'Click **↻ Refresh** to update. If it says “Could not load dashboard”, click **↻ Retry**.' },
        ],
      },
      { h: 'Jump into any module', d: 'One click into any department.',
        steps: [
          'Scroll to **Quick Access — All Modules**.',
          'Find the department card (Reception, Waiter, Kitchen, Cashier, Accounting, Payroll…).',
          'Click **Open →** on the card.',
        ],
        result: 'You see that department exactly as its staff see it, with its own guide.',
      },
    ],
  },
  admin_reporting: {
    title: 'Reports & Analytics',
    intro: 'Department-by-department reports for any period, ready to export.',
    faq: [{ q: 'The report is empty', a: 'Check the period. Choose a wider range (This Month or Custom Range) and click Generate again.' }],
    sections: [
      { h: 'Run a report', d: 'Get figures for any department and any period.',
        steps: [
          { t: 'Choose a report card: **Financial Overview**, **Rooms & Occupancy**, **Food & Beverage**, **HR & Payroll**, **Stock & Procurement**, **Guest & Reservations**, **Security & Gate** or **Maintenance**.' },
          'Click **Generate Report →** on that card.',
          { t: 'Pick the period: **Today**, **This Week**, **This Month**, **This Year** or **Custom Range**.', tip: 'For Custom Range, set **From Date** and **To Date** and then click **Generate**.' },
          'Read the summary tiles and tables.',
          { t: 'Click **⬇ Excel** or **⬇ PDF** to download it.', tip: 'Excel is best for analysis; PDF is best for printing or emailing.' },
          'Click **← All Reports** to choose another.',
        ],
        result: 'A downloaded report you can share with owners or auditors.',
        mistakes: ['Expecting a full all-department export from a single department report — use Financial Overview for that.'],
      },
    ],
  },
  admin_reports: {
    title: 'Financial Reports',
    intro: 'Profit and loss for any period, and a full gift shop sales export.',
    sections: [
      { h: 'Produce a profit and loss statement', d: 'Revenue, costs and profit for a period.',
        steps: [
          'Choose **This Week**, **This Month**, **This Year** or **Custom Range** (enter **From Date** and **To Date**).',
          'Click **Generate Report**.',
          'Read **Total Revenue**, **Room Revenue**, **F&B Revenue**, **Total Expenses** and **Net Profit**.',
          { t: 'Check **Revenue by Department** and **Expense Breakdown** to see where money comes from and goes.' },
          { t: 'Read the P&L block: Gross Revenue − VAT (16%) − Catering Levy (2%) = Net Revenue, then minus expenses = Net Profit/(Loss).', tip: 'Prices in the system include VAT, so this section shows what is truly yours.' },
          'Review **Staff Performance** for revenue per staff member.',
        ],
        result: 'A clear profit picture for management meetings.',
      },
      { h: 'Export all gift shop sales', d: 'Every gift shop sale for a date range.',
        steps: [
          'In **Gift Shop Sales Export**, set **DATE FROM** and **DATE TO**.',
          'Export. The file lists every sale with items, payment modes and a product summary.',
        ],
      },
    ],
  },
  admin_users: {
    title: 'Staff Accounts',
    intro: 'Give every employee their own login, with only the screens their job needs.',
    faq: [
      { q: 'A staff member forgot their password', a: 'Employee Profiles → open them → 🔒 Security → Change Password.' },
      { q: 'The new user cannot see their screen', a: 'Check the Role. Each role only shows its own screens. Kitchen/Chef also need the right Kitchen Station.' },
    ],
    sections: [
      { h: 'Create a staff account', d: 'Give a new employee their own login.',
        steps: [
          { t: 'In **New Staff Account**, type the **Full Name** (e.g. John Doe).' },
          { t: 'Type a **Username** — short, lowercase, no spaces (e.g. john or j.kamau).', tip: 'Usernames must be unique. Staff type it every time they log in.' },
          { t: 'Type a starting **Password**.', tip: 'Tell the employee to keep it private. You can change it later in Employee Profiles → Security.' },
          { t: 'Choose the **Role**.', tip: 'Pick the lowest role that lets them do their job: Waiter, Cashier, Reception, Kitchen, Chef, Housekeeping, Gate, Storekeeper, Gift Shop, Accountant, HR, Marketing, Restaurant Manager, General Manager, Swimming Attendant, Kids Amenities, Driver or Admin.' },
          { t: 'For Kitchen or Chef, type the **Kitchen Station** (e.g. Main Kitchen, Poolside Kitchen).', tip: 'They will only see orders sent to that station, so use exactly the same name waiters will pick.' },
          'Click save. Give the employee their username, password and the address m-hotels.co.ke/#/staff.',
        ],
        result: 'The employee can log in and sees only their own screens.',
        mistakes: ['Giving everyone the Admin role — they would see salaries, settings and could delete data.', 'Sharing one account between several people.'],
      },
      { h: 'Suspend or remove an account', d: 'Stop access when someone is suspended or leaves.',
        steps: [
          { t: 'To suspend temporarily, use the account’s active toggle to deactivate it.', tip: 'Reactivate the same way when they return.' },
          { t: 'For someone who has left, click **Remove**.' },
          { t: 'Type a reason in **Reason for removal (optional)** (e.g. Resigned, Contract ended) and click **Confirm Remove**.', tip: 'Nothing is lost: all their orders, payments and audit records are kept.' },
          'Removed accounts move to a separate list for reference.',
        ],
        result: 'The person can no longer log in; their history stays intact.',
      },
    ],
  },
  admin_employee_profiles: {
    title: 'Employee Profiles',
    intro: 'Everything about one employee: details, logins, attendance, activity and password reset.',
    sections: [
      { h: 'Look up an employee', d: 'Find anyone quickly.',
        steps: [
          'Type in **Search name or username…** or filter by role with **All Roles**.',
          'Each card shows **Days Worked**, **Total Logins** and **Total Actions**.',
          'Click an employee to open their profile.',
        ],
      },
      { h: 'Review their record', d: 'Logins, attendance and what they did.',
        steps: [
          '**👤 Overview** — profile details and account information.',
          '**🔑 Login History** — every login with date and time.',
          '**📅 Attendance** — clock-in and clock-out records.',
          '**📋 Activity Log** — every action they took in the system.',
        ],
      },
      { h: 'Reset a password', d: 'When someone forgets their password or it may be known to others.',
        steps: [
          'Open the employee and go to **🔒 Security**.',
          { t: 'Under **🔒 Change Password**, type the new password (at least 4 characters).', tip: 'Use something they can remember but others cannot guess.' },
          'Save. A green message confirms “Password updated”.',
          'Give them the new password privately.',
        ],
        result: 'They are logged out everywhere and must log in with the new password.',
      },
    ],
  },
  admin_guest_history: {
    title: 'Guest History',
    intro: 'Every guest who ever stayed, with all their stays, charges and payments.',
    sections: [
      { h: 'Find a guest', d: 'See any past or current guest’s history and spending.',
        steps: [
          'Type in **Search name, ID number, phone, email, room…**.',
          'Check the status: **● In-House**, **✓ Checked Out** or **🗂 Archived**.',
          'Open the guest to see every stay, folio charge and payment.',
        ],
        result: 'Before a returning guest arrives you know their history and preferences.',
      },
      { h: 'Archive a guest record', d: 'Remove a guest from active lists without losing anything.',
        steps: [
          'Open the guest and click **🗂 Archive Guest Record**.',
          'Confirm with **Archive**.',
        ],
        result: 'The guest disappears from active lists but every stay, charge and payment stays viewable here.',
      },
    ],
  },
  admin_bulk_import: {
    title: 'Bulk Import',
    intro: 'Load many rooms, menu items or store items at once from a spreadsheet instead of typing them one by one.',
    faq: [{ q: 'Some rows failed', a: 'Failed rows are listed with the reason. Fix them in your CSV and import again with ↩ Import More.' }],
    sections: [
      { h: 'Import from a spreadsheet', d: 'Hundreds of records in minutes.',
        steps: [
          { t: 'Under **1. Choose what to import**, pick **Rooms**, **Food Menu**, **Bar Menu** or **Store Items**.' },
          { t: 'Under **2. Download template & fill it in**, click **⬇ Download CSV Template**.', tip: 'The required columns are listed on screen. Do not rename or remove the header row.' },
          { t: 'Open the template in Excel or Google Sheets, fill one row per item, then save it as **CSV**.', tip: 'In Excel: File → Save As → CSV (Comma delimited).' },
          { t: 'Under **3. Paste CSV data or upload your filled template**, click **📂 Upload CSV file** — or paste the rows into the box.' },
          { t: 'Check the preview. Fix any row marked with an error until it says **✓ All valid**.', tip: 'Common errors: an empty required column, letters in a price, or a duplicate room number.' },
          'Click import and read the result.',
        ],
        result: 'The records appear immediately in Rooms, the menus or the Storeroom.',
        mistakes: ['Saving the file as .xlsx instead of .csv.', 'Ignoring failed rows — they were not saved. Fix them and use ↩ Import More.'],
      },
    ],
  },
  admin_rooms: {
    title: 'Rooms',
    intro: 'The rooms you sell, their prices and their status.',
    sections: [
      { h: 'Add a room', d: 'Set up the rooms reception can sell.',
        steps: [
          'In **🏨 New Room**, type the **Room Number** (e.g. 105).',
          'Choose the **Type** (Standard, Deluxe, Suite, Presidential…).',
          'Type the **Floor** (e.g. 1) and the **Price/Night (KES)** (e.g. 5500).',
          'Save. A message confirms “Room added!”.',
        ],
        result: 'The room appears in Reception’s check-in list and on the housekeeping board.',
      },
      { h: 'Remove a room or take it out of service', d: 'Stop selling a room.',
        steps: [
          { t: 'To stop selling a room for repairs, set its status to **Maintenance**.', tip: 'It stays in the system and is excluded from occupancy until you set it back.' },
          { t: 'To delete a room permanently, click **Remove**.', tip: 'An occupied room cannot be removed — check the guest out first.' },
        ],
      },
      { h: 'Export the room status list (admin only)', d: 'Print or share the current state of every room.',
        steps: [
          'In **🏨 Room Status Export**, filter by **HK Status Filter** (Clean, Dirty, Inspecting, Out of Order).',
          'Optionally filter by **Occupancy Status**, **Floor** and **Room Type**.',
          'Export to Excel or PDF.',
        ],
        result: 'A room list for the morning meeting or the night audit.',
      },
    ],
  },
  admin_menu: {
    title: 'Food / Bar Menu',
    intro: 'The dishes and drinks waiters can sell, with prices.',
    sections: [
      { h: 'Add a menu item', d: 'Make a dish or drink available to waiters.',
        steps: [
          'In **＋ New Menu Item**, type the **Item Name** (e.g. Beef Stew).',
          'Type the **Price (KES)** (e.g. 750).',
          { t: 'Choose a **Category** — or type a new one in **or type new**.', tip: 'Categories group the menu for waiters (Grills, Soups, Cocktails…).' },
          'Save. Waiters see it immediately.',
        ],
      },
      { h: 'Remove an item', d: 'Take something off the menu.',
        steps: [
          'Find the item and click **Delete**.',
          { t: 'To hide an item only for today (out of stock), use Restaurant Manager → Menu & Pricing and untick Available for ordering instead.', tip: 'That keeps its history and lets you bring it back easily.' },
        ],
      },
    ],
  },
  admin_gate_log: {
    title: 'Gate Log',
    intro: 'Security records of every visitor who entered and left.',
    sections: [
      { h: 'Review visitors', d: 'Check who came in, who is still inside and for how long.',
        steps: [
          'Read the totals: **Currently Inside**, **With Vehicles** and **Avg Time on Premise**.',
          'Use **Visitor Breakdown by Type** to see visitor mix.',
          'Filter by **Status** (Inside / Exited), **Visitor Type**, dates, or search name, ID, plate or phone.',
          'Click a row for full details, including time on premises.',
        ],
      },
      { h: 'Export the log', d: 'For security records or an investigation.',
        steps: [
          'Set **Date From** and **Date To (export)** and any filters.',
          { t: 'Export.', tip: 'The file includes name, ID, phone, plate, type, purpose, host, time in, time out, duration, and the officers who recorded entry and exit.' },
        ],
      },
    ],
  },
  admin_branding: {
    title: 'Branding',
    intro: 'Your hotel’s name, logo, stamps and look — used on every screen, receipt, export and shared link.',
    faq: [{ q: 'The name changed then went back', a: 'Click Save and wait for “Branding saved — all exports updated ✓”. If it says permission denied, you are not logged in as an admin.' }],
    sections: [
      { h: 'Set the hotel identity', d: 'Name and contact details shown everywhere.',
        steps: [
          'Open **Hotel Identity**.',
          'Type the **Hotel Name** and an optional **Tagline**.',
          'Type **Phone**, **Email** and **Address** — they print on invoices and receipts.',
          { t: 'Set a **Symbol** — shown when there is no logo.' },
          { t: 'Click **Save** at the bottom.', tip: 'Wait for the green “Branding saved — all exports updated ✓” message.' },
        ],
        result: 'The new name shows in the sidebar, browser tab, landing page, receipts, exports and link previews on WhatsApp/Facebook.',
      },
      { h: 'Upload the logo', d: 'Put your logo on screens, receipts and the website.',
        steps: [
          'Open **Logo**.',
          { t: 'Click or drag & drop your logo onto the box (**Upload Logo**).', tip: 'PNG, JPG or SVG, max 500 KB. A square or wide logo with a transparent background looks best.' },
          'Click **Save**. Use **Change** later to replace it.',
        ],
      },
      { h: 'Add approval stamps', d: 'Official stamps printed on approved documents.',
        steps: [
          'Open **Approval Stamp** and choose **Storekeeper**, **Accountant**, **Manager** or **Director**.',
          { t: 'Drag & drop or click to upload the stamp image.', tip: 'PNG with a transparent background, max 1 MB. Scan or photograph your rubber stamp on white paper.' },
          'Repeat for each role. **Replace** changes a stamp; removing it falls back to the built-in stamp.',
        ],
        result: 'Approved vouchers, shopping lists, complimentary authorizations and payslips print with the right stamp.',
      },
      { h: 'Change colours, mode and fonts', d: 'Match the system to your brand look.',
        steps: [
          'Open **Colors** and pick one of the **Quick Presets** — or set **Primary Color**, **Primary Hover**, **Accent / Success** and **Danger / Error** yourself.',
          'Check the **Preview** boxes.',
          'Open **Display Mode** and choose **Light Mode** or **Dark Mode**.',
          { t: 'Open **Typography** and type a **Display / Heading Font** and **Body / UI Font**.', tip: 'Use common fonts (e.g. Poppins, Inter) — they must be system or Google fonts.' },
          'Click **Save**. **Reset to Defaults** restores the original look.',
        ],
        result: 'The look changes immediately on this device.',
        mistakes: ['Expecting colours to change on other computers — colours, mode and fonts are saved per device; the name and logo are saved for everyone.'],
      },
    ],
  },
  admin_landing: {
    title: 'Landing Page Editor',
    intro: 'Everything guests see on your public website. Changes go live the moment you save.',
    sections: [
      { h: 'Edit the website text', d: 'The words guests read.',
        steps: [
          'Open **📝 Text Content**.',
          '**Hero Section**: the small location tag, the big headline (e.g. Where the Wild Meets Luxury) and the description paragraph.',
          { t: '**Why Choose Us Section**: heading, body paragraph and up to 6 bullet points (one per line).', tip: 'Type \\n in a heading to force a line break.' },
          '**Mid-Page Banner Section**: banner headline and body text. **Footer Tagline**: one short line.',
          'Save.',
        ],
      },
      { h: 'Change the photos', d: 'The pictures on your website.',
        steps: [
          'Open **🖼️ Images**.',
          'Choose the slot: **Hero Background**, **Banner Section**, **Room Cards Image** or one of the 4 **Gallery Images**.',
          { t: 'Click **⬆ Upload** to pick a photo — or **🔗 URL**, paste a link and click **Set**.', tip: 'PNG, JPG or WebP up to 2 MB. Use bright, wide (landscape) photos.' },
          '**✕ Remove** clears a slot. Save.',
        ],
      },
      { h: 'Update rooms and amenities', d: 'Room types, prices and facilities shown to guests.',
        steps: [
          'Open **🛏️ Rooms** (or **✨ Amenities**).',
          { t: 'Click **Load Template** to start from an example.', tip: 'The list is in JSON. Only change the text between quotes and the numbers; keep every comma, bracket and quote.' },
          'Edit names, prices, descriptions and features. Save. **Reset** restores the default list.',
        ],
        mistakes: ['Deleting a comma or bracket — the editor will refuse to save. Undo your last change and try again.'],
      },
      { h: 'Choose a layout style', d: 'Change the overall look of the website.',
        steps: [
          'Open **🎨 Layout Style**.',
          'Click **Classic**, **Modern**, **Minimal**, **Luxury** or **Resort**.',
          'Save, then open the website in a new tab to see it.',
        ],
      },
    ],
  },
  admin_reviews: {
    title: 'Guest Reviews',
    intro: 'Feedback guests submit on your website.',
    sections: [
      { h: 'Read guest feedback', d: 'See what guests say about you.',
        steps: [
          'Scroll the reviews, newest first, with rating and category.',
          'Type in **🔍 Search reviews…** to find a topic (e.g. breakfast, noise).',
          'Filter with **All Categories** and **All Ratings**. **✕ Clear** resets.',
          { t: 'For a poor review, log a complaint in **Guest CRM** so someone follows up.' },
        ],
        result: 'You spot recurring problems early and fix them.',
      },
    ],
  },
  admin_complimentary: {
    title: 'Complimentaries',
    intro: 'Free meals or services given by staff need a manager’s approval before they are expensed.',
    sections: [
      { h: 'Approve or reject a complimentary', d: 'Control free meals and services.',
        steps: [
          'Open a request under **Pending Approval**. Check guest, category, amount and **Reason**.',
          { t: 'If justified, click **✅ Approve & Expense**.', tip: 'It is recorded as an expense, not as a sale, so revenue stays accurate.' },
          { t: 'If not, click **❌ Reject**, type a reason and **Confirm Reject**.' },
          'Click **🖨️ Print Authorization** to print it with the manager stamp for the file.',
        ],
        result: 'Totals update: pending, approved and total expensed.',
      },
    ],
  },
  admin_donations: {
    title: 'Donations',
    intro: 'A record of every donation received.',
    sections: [
      { h: 'Record a donation', d: 'Keep a proper record of every donation.',
        steps: [
          'In **Record New Donation**, type the **Donor Name** (and email if known).',
          { t: 'Choose the **Currency**.', tip: 'Foreign currency is converted to KES using the rates in Currencies; the KES equivalent is shown.' },
          'Type the **Amount**, choose the **Payment Mode** and enter the **Transaction Ref** (M-Pesa code or receipt number).',
          'Add notes and click **✓ Record Donation**.',
        ],
        result: 'The donation is saved and the totals update.',
      },
    ],
  },
  admin_currencies: {
    title: 'Currencies',
    intro: 'Exchange rates so staff can accept foreign currency correctly.',
    sections: [
      { h: 'Add or update an exchange rate', d: 'Accept dollars, euros and others at the right rate.',
        steps: [
          { t: 'Click a **Quick add** button for a common currency — or type the **Currency Code** (e.g. USD), **Currency Name** (US Dollar) and **Symbol** ($).' },
          { t: 'Type the **Rate (1 unit = X KES)** — e.g. 1 USD = 129.50 KES.', tip: 'Use your bank’s buying rate.' },
          'Click **✓ Save**. Use **✏️ Edit** to change a rate later.',
        ],
        result: 'Rates are used for multi-currency payments, donations and reports.',
        mistakes: ['Leaving old rates for months — update them at least weekly.'],
      },
    ],
  },
  admin_leave: {
    title: 'Leave Requests',
    intro: 'Approve staff leave and see leave patterns.',
    sections: [
      { h: 'Approve or reject leave', d: 'Decide on leave requests.',
        steps: [
          'Open **🗂️ Manage Requests**. Search by employee, leave type or reason.',
          'Open a pending request and check the dates against your staffing.',
          'Add an **Admin note (optional)**.',
          'Click **✅ Approve** — or **Reject** with a reason.',
        ],
        result: 'The employee is notified. Staff on approved leave have their account locked until they return.',
      },
      { h: 'Let someone back in after leave', d: 'Unlock their account when they return.',
        steps: ['Find the employee marked **🔒 Account currently locked**.', 'Click **🔓 Reactivate Staff**.'],
      },
      { h: 'Look at leave reports', d: 'Spot patterns and plan cover.',
        steps: [
          'Open **📊 Leave Reports**.',
          'Read **Requests by Status**, **Requests by Leave Type** and the **Monthly Leave Trend (Last 12 Months)**.',
          'See total and average days taken per employee.',
        ],
      },
    ],
  },
  admin_attendance: {
    title: 'Attendance',
    intro: 'Who worked when — for payroll and discipline.',
    sections: [
      { h: 'Record attendance for a whole team', d: 'Fast entry for many people at once.',
        steps: [
          'Open **Bulk Entry**. Pick the **Date** and **Department**.',
          'Use **Mark all as** to set everyone present (or absent).',
          'Change individuals who differ and add a note if needed.',
          'Save. A message confirms how many records were saved.',
        ],
      },
      { h: 'Add or correct one record', d: 'When someone forgot to clock in or out.',
        steps: [
          'Open **Records** and click **+ Add**.',
          'Choose the **Employee**, set **Clock In** and **Clock Out**, add a note and save.',
          'To fix an existing record, click **Edit** on it.',
          'Records show **Self** (clocked by the employee) or **Admin** (entered by you).',
        ],
      },
      { h: 'Monthly attendance report', d: 'For payroll at month end.',
        steps: ['Open **Monthly Report**.', 'Choose the **Month** and click **Load Report**.'],
      },
    ],
  },
  admin_session: {
    title: 'Session Settings',
    intro: 'Rules for how and when staff can log in.',
    sections: [
      { h: 'Set login rules', d: 'Control logins for security.',
        steps: [
          { t: '**Session Duration**: hours before staff are logged out automatically. 0 = never.', tip: '12 hours suits most shifts.' },
          { t: 'Turn on **Single Session Only** so logging in on a new device logs out the old one.', tip: 'Stops staff sharing one account on several devices.' },
          { t: 'Turn on **Login Time Window** and set **Allow Login From** and **Block Login After** (e.g. 06:00 and 23:00).', tip: 'Outside the window staff see “Login not permitted outside 06:00–23:00”.' },
          'Save. A message confirms “Session settings saved”.',
        ],
        result: 'Rules apply from the next login. Admin accounts are always exempt.',
        mistakes: ['Setting a login window that excludes night staff (gate, night reception).'],
      },
    ],
  },
  admin_backup: {
    title: 'Data Backup',
    intro: 'Keep a copy of your settings and data in case of a problem.',
    sections: [
      { h: 'Download a backup', d: 'Do this weekly and before any big change.',
        steps: [
          'Click **⬇ Download Backup Now**.',
          { t: 'Save the .json file somewhere safe that is NOT this computer (Google Drive, email to yourself, USB).', tip: 'Last Backup shows when you last did it.' },
        ],
        result: 'A file with branding, stamps, settings and a snapshot of guests, orders, rooms, staff and menus.',
      },
      { h: 'Restore from a backup', d: 'Bring back settings after a problem.',
        steps: [
          'Under **📤 Restore from Backup**, choose a previously downloaded .json file.',
          { t: 'Confirm. Settings and branding are restored.', tip: 'Database records (guests, orders, payments) are restored from the server’s own database backup by your technical person.' },
        ],
      },
    ],
  },
  admin_devtools: {
    title: 'Dev Tools',
    intro: 'For the system developer only.',
    sections: [
      { h: 'Do not use on the live system', d: 'Direct database access for the developer.',
        steps: [
          'Requires a developer ID and the secret token from the server settings.',
          'Every action is logged. If you are not the developer, close this section.',
        ],
      },
    ],
  },
  admin_conference_bookings: {
    title: 'Conference Bookings',
    intro: 'All event bookings: confirm them, take payment and print invoices.',
    sections: [
      { h: 'Confirm a booking request', d: 'Turn a request into a confirmed event.',
        steps: [
          'Open **Pending**. Search by organiser, event, reference or organisation, or filter by **Event Date**.',
          'Click **View**. Check the venue rate, extras and **TOTAL**.',
          'Click **✓ Confirm**. The organiser’s booking becomes Confirmed.',
        ],
      },
      { h: 'Take payment', d: 'Record what the client paid.',
        steps: [
          'Open the booking and click **💳 Process Payment**.',
          'Choose **Cash**, **M-Pesa**, **Card**, **Bank Transfer** or **Invoice**, and type the **Transaction / Ref Code**.',
          'Click **✓ Confirm Payment**. The booking moves to **Completed**.',
          'Click **🖨 Print Invoice** for the client.',
        ],
      },
      { h: 'Cancel a booking', d: 'When the client calls it off.', steps: ['Open the booking and click **✕ Cancel**.'] },
    ],
  },
  admin_transport: {
    title: 'Transport',
    intro: 'Airport transfers and trips: create, bill and track them, and set your pickup zones and prices.',
    sections: [
      { h: 'Create and bill a trip', d: 'Book transport and charge for it in one go.',
        steps: [
          'Click **+ New Request**. Type the **Guest / Requester Name** and **Phone**.',
          { t: 'Choose the **Pickup Location** zone — or **Other / Custom location…** and type the address. Type the **Drop-off Location**.' },
          'Set **Pickup Date & Time** and **Passengers**, and choose the **Vehicle Type**.',
          { t: 'Enter the **Fee Charged to Guest (KES)** and the **Expense / Fuel Cost (KES)**.', tip: 'The net profit on the trip is shown so you can check the price.' },
          'Choose the payment mode (or **Room Charge**) and code, link the hotel guest if staying, add notes (flight number).',
          'Click **✓ Create & Bill**. **🖨 Print Receipt** if needed.',
          'After the trip, click **✓ Mark Completed**.',
        ],
        result: 'The fee is posted to Payments and the fuel cost to Expenses automatically; the driver sees the trip.',
      },
      { h: 'Set up pickup zones and prices', d: 'Standard routes with fixed prices.',
        steps: [
          'Click **+ Add Zone**. Type the **Zone Name** (e.g. JKIA Airport) and a description guests will see.',
          { t: 'Enter **Per-Vehicle Fees (KES)** for Saloon, SUV/4x4, Mini Van and Shuttle Bus.', tip: 'Set 0 for a vehicle you do not offer on that route.' },
          'Set the **Display Order** (lower appears first) and keep it **✅ Active**.',
          'Save. Use **✎ Edit** to change prices; set **⏸ Inactive** to hide a zone.',
        ],
      },
    ],
  },
  admin_kitchen_prep: {
    title: 'Kitchen Prep Report',
    intro: 'How long orders take from placement to ready — find slow stations and dishes.',
    sections: [
      { h: 'Measure kitchen speed', d: 'Find bottlenecks in the kitchen.',
        steps: [
          'Choose the **PERIOD** (and dates) and a **STATION** — or All Stations.',
          'Click **Run Report**.',
          { t: 'Read **Orders Timed**, **Avg Wait Time** (placed → started), **Avg Prep Time** (started → ready) and **Avg Total Time**.' },
          'Check **📍 By Kitchen Station**. Click column headers to sort.',
        ],
        result: 'You can see which station or dish is slow and act on it.',
        mistakes: ['Expecting all orders to be timed — only orders the kitchen marked ▶ Start Preparing are measured.'],
      },
    ],
  },
};
